import React, { useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Button,
    IconButton,
    Chip,
    Tooltip,
    CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getContratos, upsertContrato } from '../../services/contratoService';
import { getAnexoUrl } from '../../utils/storageUtils';
import { ContratoItem, ContratoInput } from '../../services/types';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { ContratoModal } from './ContratoModal';

interface ObraContratosTabProps {
    obraId: number;
}

export const ObraContratosTab: React.FC<ObraContratosTabProps> = ({ obraId }) => {
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingContrato, setEditingContrato] = useState<ContratoItem | null>(null);

    const { data: contratos = [], isLoading, error } = useQuery({
        queryKey: ['contratos', obraId],
        queryFn: () => getContratos(obraId)
    });

    const upsertMutation = useMutation({
        mutationFn: (input: ContratoInput) => upsertContrato(input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contratos', obraId] });
        }
    });

    const handleAddClick = () => {
        setEditingContrato(null);
        setIsModalOpen(true);
    };

    const handleEditClick = (contrato: ContratoItem) => {
        setEditingContrato(contrato);
        setIsModalOpen(true);
    };

    const handleSave = async (input: ContratoInput) => {
        await upsertMutation.mutateAsync(input);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Ativo': return 'success';
            case 'Finalizado': return 'default';
            case 'Cancelado': return 'error';
            default: return 'default';
        }
    };

    const handleFileClick = async (path: string) => {
        try {
            const url = getAnexoUrl('documentos', path);
            if (url) window.open(url, '_blank');
        } catch (error) {
            console.error('Erro ao abrir arquivo:', error);
        }
    };

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    if (error) return <Typography color="error">Erro ao carregar contratos.</Typography>;

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Gestão de Contratos</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
                    Novo Contrato
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Fornecedor</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Valor Total</TableCell>
                            <TableCell>Início</TableCell>
                            <TableCell>Fim</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Arquivo</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contratos.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">Nenhum contrato cadastrado.</TableCell>
                            </TableRow>
                        ) : (
                            contratos.map((contrato) => (
                                <TableRow key={contrato.id}>
                                    <TableCell>{contrato.fornecedor_nome}</TableCell>
                                    <TableCell>{contrato.descricao}</TableCell>
                                    <TableCell>{formatCurrency(contrato.valor_total)}</TableCell>
                                    <TableCell>{formatDate(contrato.data_inicio || '')}</TableCell>
                                    <TableCell>{formatDate(contrato.data_fim || '')}</TableCell>
                                    <TableCell align="center">
                                        <Chip label={contrato.status} color={getStatusColor(contrato.status) as any} size="small" />
                                    </TableCell>
                                    <TableCell align="center">
                                        {contrato.arquivo_url && (
                                            <Tooltip title="Visualizar Contrato">
                                                <IconButton size="small" onClick={() => handleFileClick(contrato.arquivo_url!)}>
                                                    <AttachFileIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => handleEditClick(contrato)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ContratoModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSave}
                obraId={obraId}
                contratoToEdit={editingContrato}
            />
        </Box>
    );
};
